import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Dropdown, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Commandes() {
    const navigate = useNavigate();
    const [commandes, setCommandes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_commande', direction: 'desc' });
    
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [commandeToDelete, setCommandeToDelete] = useState(null);

    useEffect(() => {
        fetchCommandes();
        fetchStats();
    }, [filtreStatut]);

    const fetchCommandes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php';
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCommandes(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (cmd) => {
        setCommandeToDelete(cmd);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!commandeToDelete) return;
        
        setIsDeleting(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php?id=${commandeToDelete.id_commande_fournisseur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setCommandeToDelete(null);
                fetchCommandes();
                fetchStats();
                // Optionnel : Ajouter une alerte de succès
            } else {
                alert(result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/commandes_fournisseurs.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', icon: 'ri-draft-line', label: 'Brouillon' },
            envoyee: { color: 'info', icon: 'ri-send-plane-line', label: 'Envoyée' },
            recue_partielle: { color: 'warning', icon: 'ri-stack-line', label: 'Partiellement reçue' },
            recue_complete: { color: 'success', icon: 'ri-check-double-line', label: 'Reçue' },
            annulee: { color: 'danger', icon: 'ri-close-circle-line', label: 'Annulée' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2 rounded-pill"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const estEnRetard = (dateLivraison) => {
        return new Date(dateLivraison) < new Date();
    };

    const filteredCommandes = useMemo(() => {
        return commandes
            .filter(c => 
                c.numero_commande?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.fournisseur_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                c.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_commande') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_commande) - new Date(b.date_commande)
                        : new Date(b.date_commande) - new Date(a.date_commande);
                }
                if (sortConfig.key === 'montant_ttc') {
                    return sortConfig.direction === 'asc' 
                        ? (a.montant_ttc || 0) - (b.montant_ttc || 0)
                        : (b.montant_ttc || 0) - (a.montant_ttc || 0);
                }
                return 0;
            });
    }, [commandes, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCommandes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                size="sm" 
                onClick={onClick} 
                className="action-btn"
                style={{
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                }}
            >
                <i className={icon} style={{ fontSize: '1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des commandes...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchCommandes}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-shopping-cart-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Commandes fournisseurs
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Gérez vos achats et suivez vos commandes
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/achats/commandes/nouveau')}
                                    className="btn-gradient px-4 py-2 d-flex align-items-center gap-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '0.6rem 1.5rem',
                                        fontWeight: 500,
                                        boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className="ri-add-line" style={{ fontSize: '1.2rem' }}></i>
                                    <span>Nouvelle commande</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-shopping-cart-line text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes du mois</small>
                                                <h4 className="mb-0 fw-bold text-primary">{stats.mois?.nb_commandes || 0}</h4>
                                                <small className="text-muted">{formatCurrency(stats.mois?.total_montant || 0)}</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-time-line text-warning fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">En attente</small>
                                                <h4 className="mb-0 fw-bold text-warning">
                                                    {stats.statuts?.find(s => s.statut === 'envoyee')?.nb || 0}
                                                </h4>
                                                <small className="text-muted">à recevoir</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-check-double-line text-success fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Reçues</small>
                                                <h4 className="mb-0 fw-bold text-success">
                                                    {(stats.statuts?.find(s => s.statut === 'recue_complete')?.nb || 0) + 
                                                     (stats.statuts?.find(s => s.statut === 'recue_partielle')?.nb || 0)}
                                                </h4>
                                                <small className="text-muted">complètes/partielles</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                                                <i className="ri-alert-line text-danger fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">En retard</small>
                                                <h4 className="mb-0 fw-bold text-danger">{stats.retard?.nb || 0}</h4>
                                                <small className="text-muted">livraison</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher commande, fournisseur..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="tous">📋 Tous les statuts</option>
                                        <option value="brouillon">📝 Brouillons</option>
                                        <option value="envoyee">📤 Envoyées</option>
                                        <option value="recue_partielle">📦 Partiellement reçues</option>
                                        <option value="recue_complete">✅ Reçues</option>
                                        <option value="annulee">❌ Annulées</option>
                                    </Form.Select>
                                </Col>
                                <Col md={5} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count bg-primary text-white px-3 py-1 rounded-pill">
                                            {filteredCommandes.length}
                                        </span>
                                        <span className="text-muted ms-2">commande(s)</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                            
                            {showFilters && (
                                <Row className="mt-3">
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Trier par date</option>
                                            <option>Plus récentes</option>
                                            <option>Plus anciennes</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Filtrer par fournisseur</option>
                                            <option>Fournisseurs actifs</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Liste des commandes avec pagination */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Commande</th>
                                            <SortableHeader field="date_commande">Date</SortableHeader>
                                            <th>Fournisseur</th>
                                            <th>Liv. prévue</th>
                                            <SortableHeader field="montant_ttc">Montant</SortableHeader>
                                            <th style={{minWidth:'140px'}}>Articles</th>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((cmd, index) => {
                                                const retard = estEnRetard(cmd.date_livraison_prevue) && 
                                                              !['recue_complete', 'annulee'].includes(cmd.statut);
                                                
                                                return (
                                                    <tr key={cmd.id_commande_fournisseur} className="commande-row" style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td>
                                                            <Link to={`/achats/commandes/${cmd.id_commande_fournisseur}`} className="text-primary fw-semibold text-decoration-none">
                                                                {cmd.numero_commande}
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">
                                                                <i className="ri-calendar-line me-1"></i>
                                                                {formatDate(cmd.date_commande)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/achats/fournisseurs/${cmd.id_fournisseur}`} className="text-primary">
                                                                {cmd.fournisseur_nom}
                                                            </Link>
                                                            <div><small className="text-muted">{cmd.code_fournisseur}</small></div>
                                                        </td>
                                                        <td>
                                                            <span className={retard ? 'text-danger fw-bold' : ''}>
                                                                {cmd.date_livraison_prevue ? formatDate(cmd.date_livraison_prevue) : '-'}
                                                                {retard && <Badge bg="danger" className="ms-2">En retard</Badge>}
                                                            </span>
                                                        </td>
                                                        <td className="fw-bold text-primary">{formatCurrency(cmd.montant_ttc || 0)}</td>
                                                        <td>
                                                            <Badge bg="info" className="me-1">
                                                                {cmd.nb_articles || 0} art.
                                                            </Badge>
                                                            <small className="text-muted">
                                                                Qté: {cmd.total_quantite || 0}
                                                            </small>
                                                        </td>
                                                        <td>{getStatutBadge(cmd.statut)}</td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center gap-2">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {cmd.statut === 'brouillon' && (
                                                                    <ActionButton
                                                                        variant="outline-primary"
                                                                        icon="ri-pencil-line"
                                                                        onClick={() => navigate(`/achats/commandesModifier/${cmd.id_commande_fournisseur}`)}
                                                                        tooltip="Modifier"
                                                                    />
                                                                )}
                                                                {/* {cmd.statut === 'envoyee' && (
                                                                    <ActionButton
                                                                        variant="outline-success"
                                                                        icon="ri-check-double-line"
                                                                        onClick={() => navigate(`/achats/commandesReception/${cmd.id_commande_fournisseur}`)}
                                                                        tooltip="Réceptionner"
                                                                    />
                                                                )} */}
                                                                {cmd.statut === 'brouillon' && (
                                                                    <ActionButton
                                                                        variant="outline-danger"
                                                                        icon="ri-delete-bin-line"
                                                                        onClick={() => handleDeleteClick(cmd)}
                                                                        tooltip="Supprimer"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-shopping-cart-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucune commande trouvée</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre première commande"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/achats/commandesNouveau')}
                                                            >
                                                                <i className="ri-add-line me-2"></i>Nouvelle commande
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de suppression */}
                <Modal 
                    show={showDeleteModal} 
                    onHide={() => setShowDeleteModal(false)} 
                    centered 
                    className="delete-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-danger-soft">
                                        <i className="ri-delete-bin-line text-danger"></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-danger">Supprimer la commande</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer la commande <strong>{commandeToDelete?.numero_commande}</strong> ?
                        </p>
                        
                        <Alert variant="warning" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        La suppression entraînera la perte définitive de toutes les informations 
                                        liées à cette commande. Cette action ne peut pas être annulée.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Fournisseur :</span>
                                <span className="fw-semibold">{commandeToDelete?.fournisseur_nom}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Montant :</span>
                                <span className="fw-semibold text-primary">{formatCurrency(commandeToDelete?.montant_ttc || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Date commande :</span>
                                <span className="fw-semibold">{formatDate(commandeToDelete?.date_commande)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isDeleting}
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleConfirmDelete}
                            className="px-5 rounded-pill"
                            disabled={isDeleting}
                            style={{
                                background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                border: 'none'
                            }}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Supprimer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    /* Styles existants... */
                    
                    .delete-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    .delete-modal .modal-header {
                        background: linear-gradient(135deg, #fff5f5, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .delete-modal .modal-body {
                        padding: 1rem 2rem;
                    }

                    .delete-modal .modal-footer {
                        border-top: 1px solid #e2e8f0;
                        padding: 1.5rem 2rem 2rem;
                        background: #f8fafc;
                    }

                    .icon-wrapper {
                        width: 80px;
                        height: 80px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        font-size: 40px;
                        animation: pulse 2s infinite;
                    }

                    .bg-danger-soft {
                        background: rgba(220, 53, 69, 0.15);
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .delete-modal .btn {
                        border-radius: 12px;
                        font-weight: 600;
                        padding: 0.6rem 1.5rem;
                        transition: all 0.3s ease;
                    }

                    .delete-modal .btn:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    }

                    .delete-modal .btn-outline-secondary:hover:not(:disabled) {
                        background: #64748b;
                        border-color: #64748b;
                        color: white;
                    }

                    .delete-modal .alert {
                        border: none;
                        border-radius: 12px;
                        background: linear-gradient(135deg, #fff3cd, #fff9e6);
                        border-left: 4px solid #ffc107;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                `}</style>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .action-btn {
                        opacity: 0.8;
                        transition: all 0.2s ease;
                    }

                    .action-btn:hover {
                        opacity: 1;
                        transform: translateY(-2px);
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}